import { useSession } from 'next-auth/react';
import { useRecoilValue } from 'recoil';

import { JourneyEmissions } from '@/components/modules/journey/emissions';
import { JourneyHeader } from '@/components/modules/journey/header';
import { JourneyItinerary } from '@/components/modules/journey/itinerary';
import { JourneyRatesCard } from '@/components/modules/journey/rates-card';
import { MiniMap } from '@/components/modules/mini-map';
import { usePaths } from '@/hooks/use-paths';
import { MODE_WATER, USER_ACCESS_FEATURES } from '@/lib/constants';
import { ItineraryModel } from '@/lib/models/itinerary/types';
import { getItineraryLocations } from '@/lib/models/itinerary/utils/get-itinerary-locations';
import { getItineraryMapMarkers } from '@/lib/models/itinerary/utils/get-itinerary-map-markers';
import { getItineraryTotalEmissions } from '@/lib/models/itinerary/utils/get-itinerary-total-emissions';
import { featuresState } from '@/state/features';
import { locationsState } from '@/state/locations';
import { canAccessFeature } from '@/utils/auth';
import { getTransportColor } from '@/utils/helpers/get-transport-color';
import { pathsToMapPaths } from '@/utils/mapping';
import { tv } from '@/utils/styles';

export interface ItineraryProps {
  itinerary: ItineraryModel;
}

const Itinerary = ({ itinerary }: ItineraryProps) => {
  const { data: session } = useSession();
  const canAccessRates = canAccessFeature(USER_ACCESS_FEATURES.RATES, session);
  const features = useRecoilValue(featuresState);

  const { fromId, toId, type, legs, carrierIds, rates } = itinerary;
  const shouldShowRates = features.showSpotRates && canAccessRates && type === MODE_WATER;

  const locations = useRecoilValue(locationsState);
  const itineraryLocations = getItineraryLocations(itinerary.legs, locations);

  const { paths } = usePaths(itineraryLocations, type, true);
  const pathColor = getTransportColor(type);

  const itineraryEmissions = getItineraryTotalEmissions(legs);

  const mapMarkers = getItineraryMapMarkers(itinerary.legs, locations);
  const mapPaths = pathsToMapPaths(paths, { pathColor });

  const { base, header, grid, map, content } = styles();

  return (
    <div className={base()}>
      <div className={header()}>
        <JourneyHeader originId={fromId} destinationId={toId} mode={type} carrierIds={carrierIds} />
      </div>
      <div className={grid()}>
        <div className={content()}>
          <JourneyItinerary legs={legs} />
          <JourneyEmissions mode={type} emissions={itineraryEmissions} />
          {shouldShowRates && <JourneyRatesCard rates={rates} />}
        </div>
        <div className={map({ className: 'chromatic-ignore' })}>
          <MiniMap paths={mapPaths} markers={mapMarkers} />
        </div>
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'w-full',
    grid: 'grid grid-cols-1 gap-4 px-2 py-6 md:grid-cols-[minmax(auto,700px)_auto] md:gap-8 md:px-8',
    content: 'flex flex-col gap-4',
    header: 'bg-white p-4 md:px-8 md:py-6',
    map: 'top-10 h-96 overflow-hidden rounded-lg md:sticky md:h-full md:max-h-[80vh]',
  },
});

export { Itinerary };
